import classNames from 'classnames/bind';
import { Panel } from 'components/Panel';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { omit } from 'lodash-es';
import { useReducer, useState } from 'react';
import {
  CGV,
  type CompanyEdit,
  CustomPreferencesFieldsName,
  getDefaultCgvValues,
  SalePreferencesFieldsName,
} from 'shared/forms/PreferencesForm';
import type { CompanyConfiguration } from 'shared/hooks/use-load-company-configuration';
import { reducerState } from 'shared/utils/view';
import { onBoardingActions } from 'store/onBoarding/onBoarding.actions';

import type { StepProps } from '../OnBoarding.types';
import styleIdentifiers from './SetupDebtCollection.module.scss';
import SetupDebtCollectionForm from './SetupDebtCollectionForm';

const styles = classNames.bind(styleIdentifiers);

const title = {
  1: i18nKeys.SALE_CONDITION,
  2: i18nKeys.FORM.PREFERENCES.CUSTOM_RECOVERY,
  3: i18nKeys.FORM.PREFERENCES.PREFERENCES_POSTAL_SEND,
  4: i18nKeys.GENERAL_CONDITIONS,
};

export default function SetupDebtCollection({ onValidStep }: StepProps) {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const [step, setStep] = useState(1);
  const [formValues, setFormValues] = useReducer(reducerState, {});

  const addDebtCollectionSettings = (values: CompanyEdit) => {
    onBoardingActions.setupDebtCollectionSettings({
      data: {
        ...omit(values, [CustomPreferencesFieldsName.SEND_COPY_EMAILS_INPUT]),
        [CGV]: values[CGV].map(e => omit(e, SalePreferencesFieldsName.PDF_BLOB)),
      },
      callback: () => {
        const files = values[CGV].filter(e => e[SalePreferencesFieldsName.PDF_BLOB]);

        if (!files.length) {
          return onValidStep();
        }

        return onBoardingActions.uploadTerms({
          ...files[0],
          callback: () => onValidStep(),
        });
      },
      actionFailure: (response: any) => {
        onValidStep(response.data.next_step);
      },
    });
  };

  const onSubmitStep = (values: any) => {
    setFormValues(values);
    if (step === 4) {
      addDebtCollectionSettings({ ...formValues, ...values });
    } else {
      setStep((step + 1) % 5);
    }
  };

  const defaultValues = {
    ...company,
    ...formValues,
  } as CompanyConfiguration;

  return (
    <div className={styles('setup-debt-collection')}>
      <div className={styles('steps')}>
        <Panel title={t(title[step as keyof typeof title])}>
          <SetupDebtCollectionForm
            step={step}
            previousStep={() => setStep(step - 1)}
            nextStep={onSubmitStep}
            defaultValues={
              {
                ...defaultValues,
                ...(!defaultValues[CGV].length
                  ? {
                      [CGV]: [getDefaultCgvValues(true)],
                    }
                  : undefined),
              } as CompanyConfiguration
            }
          />
        </Panel>
      </div>
    </div>
  );
}
